import styled from 'styled-components/macro';
import { Icon } from '@cognite/cogs.js';
import { RuleSort, RuleSortColumn, RuleSortDirection } from '../helpers';

const ARIA_SORT_BY_DIRECTION: Record<
  RuleSortDirection,
  'ascending' | 'descending'
> = {
  asc: 'ascending',
  desc: 'descending',
};

type SortableHeaderProps = {
  label: string;
  column: RuleSortColumn;
  activeSort: RuleSort | null;
  onSort: (column: RuleSortColumn) => void;
};

export const SortableHeader = ({
  label,
  column,
  activeSort,
  onSort,
}: SortableHeaderProps) => {
  const activeDirection: RuleSortDirection | null =
    activeSort && activeSort.column === column ? activeSort.direction : null;
  const ariaSort = activeDirection
    ? ARIA_SORT_BY_DIRECTION[activeDirection]
    : 'none';

  return (
    <SortableTh aria-sort={ariaSort}>
      <SortableHeaderButton type="button" onClick={() => onSort(column)}>
        <span>{label}</span>
        <SortIndicator>
          <SortCaret $active={activeDirection === 'asc'}>
            <Icon type="CaretUp" size={18} />
          </SortCaret>
          <SortCaret $active={activeDirection === 'desc'}>
            <Icon type="CaretDown" size={18} />
          </SortCaret>
        </SortIndicator>
      </SortableHeaderButton>
    </SortableTh>
  );
};

const SortableTh = styled.th`
  /* Doubled selector lifts specificity above StyledTable's "th { padding }" rule. */
  && {
    padding: 0;
  }
`;

const SortableHeaderButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 1rem;
  background: transparent;
  border: 0;
  font: inherit;
  font-weight: 600;
  font-size: 14px;
  color: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: var(--cogs-greyscale-grey3);
  }

  &:focus-visible {
    outline: 2px solid var(--cogs-text-icon--interactive--default);
    outline-offset: -2px;
  }
`;

const SortIndicator = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  line-height: 0;
  margin-left: auto;
`;

const SortCaret = styled.span<{ $active: boolean }>`
  display: inline-flex;
  /* Tighten the vertical gap between the two carets so the stack reads as one
     control even at larger sizes. */
  margin: -5px 0;
  color: ${({ $active }) =>
    $active ? 'var(--cogs-primary)' : 'var(--cogs-greyscale-grey5)'};
`;
